"use client";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { COVERAGE_AREA_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveCoverageArea from "@/hooks/requests/coverage-area/use-remove-coverage-area";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import ICoverageArea from "@/interfaces/coverage-area.interface";
import { CoverageAreaMessages } from "@/messages/coverage-area/coverage-area.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: CoverageAreaMessages;
  selected: ICoverageArea;
}

export function CoverageAreaPopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<ICoverageArea>();

  const { remove, isLoading: isLoadingRemove } = useRemoveCoverageArea();
  const { mutate } = useMutate();

  const hasNoPermissionToEdit = useHasNoPermission(COVERAGE_AREA_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(COVERAGE_AREA_PERMISSIONS.DELETE);

  const handleOnEdit = () => router(ROUTE_URLS.updateCoverageArea(lang, selected.id));

  const handleConfirmDelete = async (coverageArea: ICoverageArea) => {
    const _id = coverageArea.id;
    const onSuccess = () => mutate(SWR_KEYS.getCoverageAreas);
    const successMessage = messages.deleteCoverageAreaSuccess;
    const errorMessage = messages.deleteCoverageAreaError;
    const onError = () => handleClose();

    await remove({ _id, onSuccess, successMessage, errorMessage, onError });
  };

  return (
    <Menu open={isOpen} anchorEl={anchorEl} onClose={onClose}>
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={handleOnEdit}
        title={messages.updatePopover}
      />
      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selected)}
        title={messages.removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => handleConfirmDelete(selected)}
        titleText={messages.deleteTitle}
        descriptionText={messages.deleteContent}
        cancelText={messages.deleteCancel}
        confirmText={messages.deleteConfirm}
        isLoading={isLoadingRemove}
      />
    </Menu>
  );
}
